import { useContext, useEffect, useRef, type RefObject } from "react";

import { GameSurfaceIntegrated, GameSurfaceVisibility } from "../state/GameSurfaceVisibility";

/** Scope focus and keyboard handling to the active game dialog. */
export function useDialog(
  ref: RefObject<HTMLElement | null>, active: boolean, identity: unknown,
  close: () => void, choose?: (index: number) => void,
) {
  const visible = useContext(GameSurfaceVisibility);
  const integrated = useContext(GameSurfaceIntegrated);
  const latest = useRef({ close, choose });
  latest.current = { close, choose };
  useEffect(() => {
    const dialog = ref.current;
    if (!active || !visible || !dialog) return;
    const origin = document.activeElement as HTMLElement | null;
    dialog.scrollTop = 0;
    const focusables = () => Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]',
    )).filter(element => element.getClientRects().length > 0);
    dialog.focus({ preventScroll: true });
    function onKey(event: KeyboardEvent) {
      // Native workspace navigation stays reachable; hidden games never handle input.
      if (integrated && !dialog!.contains(event.target as Node)) return;
      if (event.key === "Escape") {
        event.preventDefault(); latest.current.close(); return;
      }
      if (event.key === "Tab" && !integrated) {
        const items = focusables();
        const first = items[0], last = items.at(-1);
        if (!first) { event.preventDefault(); dialog!.focus(); return; }
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog || !dialog!.contains(document.activeElement))) {
          event.preventDefault(); last?.focus();
        } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialog || !dialog!.contains(document.activeElement))) {
          event.preventDefault(); first.focus();
        }
      }
      if (!event.repeat && !event.ctrlKey && !event.metaKey && !event.altKey && /^[1-6]$/.test(event.key)
        && !(event.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) {
        event.preventDefault(); latest.current.choose?.(Number(event.key) - 1);
      }
    }
    function keepFocus(event: FocusEvent) {
      if (!integrated && !dialog!.contains(event.target as Node)) dialog!.focus({ preventScroll: true });
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("focusin", keepFocus);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("focusin", keepFocus);
      // Exit animations still keep the old dialog in the DOM briefly.
      queueMicrotask(() => { if (origin?.isConnected && (!integrated || document.activeElement === document.body)) origin.focus({ preventScroll: true }); });
    };
  }, [active, identity, ref, visible, integrated]);
}
